import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUser } from "@/hooks/useUser";
import { CheckoutSignInOptions } from "./CheckoutSignInOptions";
import { browserAuthReturnPath } from "@/lib/safeRedirect";
import { rememberCheckoutFlowForPostAuthClaim } from "@/lib/checkout-state";
import { createOtpJourneyMetadata } from "@/lib/auth/otp-journey";
import { track } from "@/lib/analytics";
import { GuestCheckoutDetails } from "./GuestCheckoutDetails";
import type { GuestRecoverySelection } from "@workspace/api-client-react";
import type { GuestCheckoutInfo } from "@/lib/guest-checkout-api";
import {
  clearCheckoutCustomerDraft, emptyCustomerDraft, readCheckoutCustomerDraft,
  saveCheckoutCustomerDraft, type CheckoutCustomerDraft,
} from "@/lib/checkout-customer-draft";

const EMAIL = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+@[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/;
function validEmailFormat(raw: string): boolean {
  const email = raw.trim();
  const [local, domain] = email.split("@");
  return email.length <= 254 && EMAIL.test(email) && local.length <= 64 &&
    !local.startsWith(".") && !local.endsWith(".") && !local.includes("..") &&
    domain.split(".").every(label => label.length <= 63);
}
const inputClass = "mt-1.5 block min-h-12 w-full rounded-xl border border-border bg-background px-3.5 py-3 text-base text-foreground shadow-sm transition-colors placeholder:text-muted-foreground focus:border-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring/30 read-only:bg-muted/50 read-only:text-muted-foreground aria-[invalid=true]:border-destructive";
const labelClass = "block text-sm font-medium text-foreground";

export function CheckoutCustomerDetails(props: {
  flowId: string; carId: number; recovery?: GuestRecoverySelection; draft?: GuestCheckoutInfo | null;
  onSaved?: (draft: GuestCheckoutInfo) => void; onEditingChange?: (editing: boolean) => void; submitting?: boolean;
}) {
  const auth = useUser();
  if (auth.startGuestCheckout && (!auth.user || auth.user.app_metadata?.checkout_guest === true)) {
    return <GuestCheckoutDetails {...props} />;
  }
  return <VerifiedCheckoutCustomerDetails {...props} />;
}

function VerifiedCheckoutCustomerDetails({ flowId, carId }: {
  flowId: string; carId: number;
}) {
  const { t } = useTranslation();
  const auth = useUser();
  const [details, setDetails] = useState(() => auth.session ? emptyCustomerDraft() : readCheckoutCustomerDraft(flowId));
  const [code, setCode] = useState("");
  const [sentEmail, setSentEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [nameTouched, setNameTouched] = useState({ firstName: false, lastName: false });
  const firstNameErrorId = useId();
  const lastNameErrorId = useId();
  const emailErrorId = useId();
  const emailHintId = useId();
  const invalidEmail = emailTouched && !validEmailFormat(details.email);
  const [cooldownUntil, setCooldownUntil] = useState(0);
  const [rateLimited, setRateLimited] = useState(false);
  const [rateLimitDuration, setRateLimitDuration] = useState(0);
  const [now, setNow] = useState(Date.now);
  const [marketing, setMarketing] = useState(false);
  const lock = useRef(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const firstNameInputRef = useRef<HTMLInputElement>(null);
  const lastNameInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const principal = useRef(auth.user?.id);
  const proof = useRef<string | undefined>(undefined);
  const journey = useRef(createOtpJourneyMetadata("/checkout"));
  const events = useRef(new Set<string>());
  const returnPath = () => browserAuthReturnPath(window.location, import.meta.env.BASE_URL);
  const emit = (stage: "anonymous_entry" | "details_continue" | "email_requested" | "email_confirmed" | "profile_ready", outcome: "presented" | "succeeded" | "failed") => {
    const key = `${stage}:${outcome}`;
    if (events.current.has(key)) return;
    events.current.add(key);
    track("checkout_identity", {
      checkout_journey_id: flowId, car_id: carId, stage, outcome,
      identity: auth.session?.user && auth.session.user.app_metadata?.checkout_guest !== true ? "signed_in" : "guest",
    });
  };
  useEffect(() => {
    if (!auth.isLoading && !auth.session) emit("anonymous_entry", "presented");
  }, [auth.isLoading, auth.session]);
  useEffect(() => {
    if (!cooldownUntil) return;
    const timer = setInterval(() => {
      const current = Date.now();
      setNow(current);
      if (current >= cooldownUntil) {
        setCooldownUntil(0);
        setRateLimited(false);
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldownUntil]);
  useEffect(() => {
    if (!sentEmail || auth.session) return;
    codeInputRef.current?.focus({ preventScroll: true });
    codeInputRef.current?.scrollIntoView?.({ block: "center", behavior: "auto" });
  }, [sentEmail, auth.session]);
  useEffect(() => {
    if (auth.user?.id) {
      clearCheckoutCustomerDraft(flowId);
      if (principal.current && principal.current !== auth.user.id) {
        setDetails(emptyCustomerDraft());
        setCode(""); setSentEmail(""); proof.current = undefined;
      }
    }
    principal.current = auth.user?.id;
  }, [auth.user?.id, flowId]);
  const change = (key: keyof CheckoutCustomerDraft, value: string) => {
    if (key === "email") setError("");
    const next = { ...details, [key]: value };
    setDetails(next);
    if (!auth.session) saveCheckoutCustomerDraft(flowId, next);
  };
  const fail = (e: unknown) => {
    const err = e as { message?: string; retryAfter?: number };
    if (typeof err?.retryAfter === "number" && Number.isFinite(err.retryAfter) && err.retryAfter > 0) {
      setError("");
      setRateLimited(true);
      setRateLimitDuration(Math.ceil(err.retryAfter));
      const at = Date.now();
      setNow(at);
      setCooldownUntil(at + err.retryAfter * 1000);
    } else setError(err?.message || t("auth.session_setup_failed"));
  };
  const send = async (force = false) => {
    if (lock.current || auth.session || Date.now() < cooldownUntil) return;
    const email = details.email.trim().toLowerCase();
    setEmailTouched(true);
    setNameTouched({ firstName: true, lastName: true });
    if (!details.firstName.trim() || !details.lastName.trim() || !validEmailFormat(email)) {
      setError("");
      const firstInvalid = !details.firstName.trim() ? firstNameInputRef.current :
        !details.lastName.trim() ? lastNameInputRef.current : emailInputRef.current;
      firstInvalid?.focus({ preventScroll: true });
      firstInvalid?.scrollIntoView?.({ block: "center", behavior: "auto" });
      return;
    }
    lock.current = true; setBusy(true); setError(""); setRateLimited(false);
    emit("details_continue", "succeeded");
    rememberCheckoutFlowForPostAuthClaim(flowId);
    try {
      const result = await auth.signInWithOtp(email, returnPath(), { force, journey: journey.current });
      if (result.error) throw result.error;
      proof.current = result.otpJourneyProof;
      setSentEmail(email);
      setCode("");
      setCooldownUntil(Date.now() + Math.max(30, result.retryAfterSec || 0) * 1000);
      setNow(Date.now());
      emit("email_requested", "succeeded");
    } catch (e) { fail(e); emit("email_requested", "failed"); }
    finally { lock.current = false; setBusy(false); }
  };
  const verify = async () => {
    if (lock.current || auth.session || !/^\d{6}$/.test(code)) return;
    lock.current = true; setBusy(true); setError("");
    try {
      const result = await auth.verifyEmailOtp(sentEmail, code, { journey: journey.current, journeyProof: proof.current });
      if (result.error) throw result.error;
      setCode(""); proof.current = undefined;
      emit("email_confirmed", "succeeded");
      // Deliberately no booking, payment, or automatic profile write.
    } catch (e) { fail(e); emit("email_confirmed", "failed"); }
    finally { lock.current = false; setBusy(false); }
  };
  const profile = auth.profile;
  const needsSetup = auth.needsProfile || (!!profile && profile.is_complete !== true);
  const needsTerms = !!(profile as typeof profile & { needs_reconsent?: boolean })?.needs_reconsent;
  const matched = !!sentEmail && auth.user?.email?.toLowerCase() === sentEmail;
  const firstName = profile?.first_name || (matched ? details.firstName : "");
  const lastName = profile?.last_name || (matched ? details.lastName : "");
  const [completion, setCompletion] = useState<{ firstName?: string; lastName?: string }>({});
  const complete = async () => {
    if (lock.current || !auth.session || auth.isLoading || auth.profileError) return;
    lock.current = true; setBusy(true); setError("");
    try {
      const res = await fetch(`${import.meta.env.BASE_URL || "/"}api/auth/${needsSetup ? "complete-profile" : "accept-terms"}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${auth.session.access_token}` },
        signal: AbortSignal.timeout(12000),
        body: JSON.stringify(needsSetup ? {
          // Saved identity fields win; unverified input is only an editable
          // suggestion for missing fields after inbox possession is proven.
          first_name: profile?.first_name || (completion.firstName ?? firstName),
          last_name: profile?.last_name || (completion.lastName ?? lastName),
          accept_terms: true, marketing_opt_in: profile?.marketing_opt_in ?? marketing,
        } : {}),
      });
      if (!res.ok) throw new Error(t("auth.save_profile_failed"));
      await auth.retryProfile();
    } catch (e) { fail(e); }
    finally { lock.current = false; setBusy(false); }
  };
  useEffect(() => {
    if (auth.session && !auth.isLoading && profile?.is_complete === true && !needsTerms) emit("profile_ready", "succeeded");
  }, [auth.session, auth.isLoading, profile, needsTerms]);
  const seconds = Math.max(0, Math.ceil((cooldownUntil - now) / 1000));
  const initials = `${profile?.first_name?.[0] ?? ""}${profile?.last_name?.[0] ?? ""}`.toUpperCase() || (auth.user?.email?.[0] ?? "").toUpperCase();
  return <section id="checkout-customer-details" className="min-w-0 rounded-2xl border border-border bg-card p-5 shadow-sm sm:p-6" data-testid="checkout-customer-details">
    <h2 className="text-lg font-semibold tracking-tight sm:text-xl">{t(sentEmail && !auth.session ? "checkout.identity.confirm_heading" : "checkout.identity.heading")}</h2>
    {error && <p role="alert" className="mt-4 rounded-xl border border-destructive/30 bg-destructive/5 px-3.5 py-2.5 text-sm text-destructive">{error}</p>}
    {rateLimited && seconds > 0 && <div className="mt-4 rounded-xl bg-muted px-3.5 py-2.5 text-sm text-muted-foreground">
      <p role="status" aria-live="polite" aria-atomic="true" className="sr-only">{t("auth.rate_limited_check", { time: `${rateLimitDuration}s` })}</p>
      <p aria-hidden="true" data-testid="checkout-rate-limit-countdown">{t("auth.rate_limited_check", { time: `${seconds}s` })}</p>
    </div>}
    {auth.session ? <>
      <div className="mt-4 flex min-w-0 items-center gap-3.5 rounded-xl border border-border bg-muted/30 p-4">
        <span aria-hidden="true" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">{initials}</span>
        <div className="min-w-0">
          <p className="truncate text-base font-semibold">{profile?.first_name} {profile?.last_name}</p>
          <p className="truncate text-sm text-muted-foreground">{auth.user?.email}</p>
        </div>
      </div>
      {auth.isLoading ? <p role="status" className="mt-4 text-sm text-muted-foreground">{t("checkout.identity.loading")}</p> :
        auth.profileError || (!profile && !auth.needsProfile) ? <button type="button" onClick={auth.retryProfile} className="mt-4 flex min-h-12 w-full items-center justify-center rounded-xl border border-foreground/25 px-4 text-sm font-semibold hover:bg-muted/50">{t("common.try_again")}</button> :
        needsSetup || needsTerms ? <form className="mt-5 space-y-4" onSubmit={e => { e.preventDefault(); void complete(); }}>
          {needsSetup && <>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className={labelClass}>{t("auth.first_name")}<input className={inputClass} required maxLength={100} autoComplete="given-name" readOnly={!!profile?.first_name} value={profile?.first_name || (completion.firstName ?? firstName)} onChange={e => setCompletion({ ...completion, firstName: e.target.value })} /></label>
              <label className={labelClass}>{t("auth.last_name")}<input className={inputClass} required maxLength={100} autoComplete="family-name" readOnly={!!profile?.last_name} value={profile?.last_name || (completion.lastName ?? lastName)} onChange={e => setCompletion({ ...completion, lastName: e.target.value })} /></label>
            </div>
            {profile?.marketing_opt_in == null && <label className="flex min-h-11 cursor-pointer items-start gap-3 text-sm leading-relaxed"><input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 accent-primary" checked={marketing} onChange={e => setMarketing(e.target.checked)} /><span>{t("checkout.identity.marketing")}</span></label>}
          </>}
          <p className="text-xs leading-relaxed text-muted-foreground">{t("checkout.identity.terms")} <a href={`${import.meta.env.BASE_URL || "/"}terms`} target="_blank" rel="noreferrer" className="font-medium text-foreground underline underline-offset-2">{t("auth.terms_of_service")}</a>{" · "}<a href={`${import.meta.env.BASE_URL || "/"}privacy`} target="_blank" rel="noreferrer" className="font-medium text-foreground underline underline-offset-2">{t("auth.privacy_policy")}</a></p>
          <button type="submit" disabled={busy} className="primary-action block min-h-12 w-full rounded-xl px-4 py-3 font-semibold">{t("checkout.identity.save")}</button>
        </form> : <div role="status" className="mt-4 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
          <p className="font-semibold">{t("checkout.guest.email_confirmed")}</p>
          <p className="mt-1">{t("checkout.identity.ready")}</p>
        </div>}
    </> : <>
      <form noValidate className="mt-5 space-y-4" onSubmit={e => { e.preventDefault(); void (sentEmail ? verify() : send()); }}>
        {!sentEmail ? <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {(["firstName", "lastName", "email"] as const).map(key => {
            const invalidName = key !== "email" && nameTouched[key] && !details[key].trim();
            const nameErrorId = key === "firstName" ? firstNameErrorId : lastNameErrorId;
            return <div key={key} className={key === "email" ? "min-w-0 sm:col-span-2" : "min-w-0"}>
            <label className={labelClass}>
            {t(key === "firstName" ? "auth.first_name" : key === "lastName" ? "auth.last_name" : "auth.email")}
            <input ref={key === "email" ? emailInputRef : key === "firstName" ? firstNameInputRef : lastNameInputRef} className={inputClass} required type={key === "email" ? "email" : "text"} maxLength={key === "email" ? 254 : 100} autoComplete={key === "firstName" ? "given-name" : key === "lastName" ? "family-name" : "email"} autoCapitalize={key === "email" ? "none" : undefined} spellCheck={key === "email" ? false : undefined} aria-invalid={(key === "email" ? invalidEmail : invalidName) || undefined} aria-describedby={key === "email" ? `${emailHintId}${invalidEmail ? ` ${emailErrorId}` : ""}` : invalidName ? nameErrorId : undefined} onBlur={() => key === "email" ? setEmailTouched(true) : setNameTouched(previous => ({ ...previous, [key]: true }))} value={details[key]} onChange={e => change(key, e.target.value)} />
            </label>
            {key === "email" && <p id={emailHintId} className="mt-1.5 text-xs leading-relaxed text-muted-foreground">{t("checkout.identity.explanation")}</p>}
            {invalidName && <p id={nameErrorId} role="alert" className="mt-1.5 text-sm font-medium text-destructive">{t(key === "firstName" ? "checkout.identity.first_name_required" : "checkout.identity.last_name_required")}</p>}
          </div>; })}
          </div>
          {invalidEmail && <p id={emailErrorId} role="alert" className="-mt-1 text-sm font-medium text-destructive">{t("auth.invalid_email")}</p>}
        </> : <>
          <p role="status" className="break-words rounded-xl bg-muted/50 px-4 py-3 text-sm leading-relaxed">{t("checkout.identity.code_sent", { email: sentEmail })}</p>
          <label className={labelClass}>{t("checkout.identity.code")}<input ref={codeInputRef} className={`${inputClass} text-center font-mono text-xl tracking-[0.5em]`} autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} /></label>
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
            <button type="button" disabled={busy || seconds > 0} onClick={() => void send(true)} className="min-h-11 text-sm font-medium underline underline-offset-4 disabled:text-muted-foreground disabled:no-underline">{seconds > 0 ? t("checkout.identity.resend_wait", { seconds }) : t("checkout.identity.resend")}</button>
            <button type="button" disabled={busy} onClick={() => { setSentEmail(""); setCode(""); setError(""); proof.current = undefined; }} className="min-h-11 text-sm font-medium underline underline-offset-4 disabled:opacity-60">{t("checkout.identity.change_email")}</button>
          </div>
        </>}
        <button type="submit" disabled={busy || auth.isLoading || (!sentEmail && seconds > 0)} className="primary-action block min-h-12 w-full rounded-xl px-4 py-3 text-base font-semibold">{busy ? t("checkout.identity.loading") : sentEmail ? t("checkout.identity.confirm") : seconds > 0 ? t("checkout.identity.resend_wait", { seconds }) : t("checkout.identity.continue")}</button>
        {!sentEmail && <p className="text-xs leading-relaxed text-muted-foreground">{t("checkout.identity.account_notice")}</p>}
      </form>
      {!sentEmail && <div className="mt-5 border-t border-border" aria-hidden="true" />}
      {!sentEmail && <CheckoutSignInOptions flowId={flowId} email={validEmailFormat(details.email) ? details.email : undefined} disabled={busy || auth.isLoading} />}
    </>}
  </section>;
}
